/**
 * The vocabulary of roles a procedure parameter can play in a report.
 *
 * A report needs to know that a parameter is the DUT's serial number or the
 * measured maximum current, whatever the template author titled it. A role
 * names that once, with the identifiers procedure templates commonly use for
 * it, so a report that asks for a role (bindings.ts) binds to any
 * procedure written to the usual convention. Where a procedure isn't, the
 * report's author binds the parameter by identifier in the report's code.
 */

export type RoleKind = 'text' | 'number' | 'choice' | 'flag' | 'file';

export type RoleGroup = 'DUT' | 'Condition' | 'Measurement' | 'Setup' | 'Data';

export interface RoleSpec {
  id: string;
  label: string;
  group: RoleGroup;
  kind: RoleKind;
  /** identifiers templates commonly use for it, matched loosely (`sameIdentifier`) */
  identifiers: string[];
}

export const ROLES: readonly RoleSpec[] = [
  { id: 'dut.application', label: 'Application name', group: 'DUT', kind: 'text', identifiers: ['application_name'] },
  { id: 'dut.assembly', label: 'Assembly name', group: 'DUT', kind: 'text', identifiers: ['assembly_name'] },
  { id: 'dut.programCode', label: 'Program code', group: 'DUT', kind: 'text', identifiers: ['program_code'] },
  { id: 'dut.partNumber', label: 'Part number', group: 'DUT', kind: 'text', identifiers: ['part_number'] },
  { id: 'dut.serial', label: 'Serial number', group: 'DUT', kind: 'text', identifiers: ['serial_number'] },
  { id: 'dut.modelYear', label: 'Model year', group: 'DUT', kind: 'text', identifiers: ['model_year'] },
  { id: 'dut.samplePhase', label: 'Sample phase', group: 'DUT', kind: 'text', identifiers: ['sample_phase'] },
  { id: 'dut.location', label: 'Location on the vehicle', group: 'DUT', kind: 'text', identifiers: ['location'] },
  { id: 'dut.hardwareVersion', label: 'Hardware version', group: 'DUT', kind: 'text', identifiers: ['hardware_version'] },
  { id: 'dut.softwareVersion', label: 'Software version', group: 'DUT', kind: 'text', identifiers: ['software_version'] },
  { id: 'dut.image', label: 'Image of the DUT', group: 'DUT', kind: 'file', identifiers: ['dut_image', 'dut_image1', 'dut_image2', 'dut_image_1', 'dut_image_2', 'image_of_the_dut', 'another_image_of_dut'] },
  { id: 'condition.function', label: 'Function (signal)', group: 'Condition', kind: 'text', identifiers: ['signal_name'] },
  { id: 'condition.side', label: 'Side', group: 'Condition', kind: 'text', identifiers: ['side'] },
  { id: 'condition.temperature', label: 'Temperature corner', group: 'Condition', kind: 'choice', identifiers: ['temperature_corner'] },
  { id: 'condition.supply', label: 'Supply corner', group: 'Condition', kind: 'choice', identifiers: ['supply_corner'] },
  { id: 'condition.supplyVoltage', label: 'Measured supply voltage', group: 'Condition', kind: 'number', identifiers: ['measure_battery_voltage_using_digital_multimeter_in_volts', 'battery_voltage', 'measured_battery_voltage'] },
  { id: 'condition.chamberTemperature', label: 'Chamber temperature', group: 'Condition', kind: 'number', identifiers: ['measured_temperature', 'measure_the_chamber_temperature', 'chamber_temperature'] },
  { id: 'condition.chamberSettled', label: 'Chamber condition met', group: 'Condition', kind: 'flag', identifiers: ['temperature_condition_met', 'chamber_temperature_condition_met'] },
  { id: 'measurement', label: 'Measured value', group: 'Measurement', kind: 'number', identifiers: [] },
  { id: 'limit.upper', label: 'Upper limit', group: 'Measurement', kind: 'number', identifiers: [] },
  { id: 'limit.lower', label: 'Lower limit', group: 'Measurement', kind: 'number', identifiers: [] },
  { id: 'setup.diagram', label: 'Setup diagram', group: 'Setup', kind: 'file', identifiers: ['diagram'] },
  { id: 'setup.photo', label: 'Setup photo', group: 'Setup', kind: 'file', identifiers: [] },
  { id: 'data.points', label: 'Points file (waveform)', group: 'Data', kind: 'file', identifiers: [] },
  { id: 'reference', label: 'Reference material for the operator', group: 'Setup', kind: 'file', identifiers: [] },
];

export type RoleId = (typeof ROLES)[number]['id'];

const BY_ID = new Map(ROLES.map((r) => [r.id, r]));

export function roleSpec(id: string | null | undefined): RoleSpec | undefined {
  return id ? BY_ID.get(id) : undefined;
}

export function isRole(id: unknown): id is RoleId {
  return typeof id === 'string' && BY_ID.has(id);
}

export function roleLabel(id: string | null | undefined): string {
  return roleSpec(id)?.label ?? (id || '');
}
